import { AuthPhase, deriveAuthPhase } from '@auth/authPhase';
import { buildReturnTo } from '@auth/buildReturnTo';
import { useAuth0 } from '@auth0/auth0-react';
import { AUTH_ERROR_MESSAGE, AUTH_ERROR_TYPE, ROUTER_NAME } from '@consts';
import { eventEmitter, EventType } from '@event-emitter';
import { useAccountsService } from '@hooks/useAccountsService';
import { useAffiliateService } from '@hooks/useAffiliateService';
import { useAuthSessionProbe } from '@hooks/useAuthSessionProbe';
import { useImprvApi } from '@hooks/useImprvApi';
import * as Sentry from '@sentry/nextjs';
import { clearReferralCode, getStoredReferralCode } from '@services/referralStorage';
import { useStore } from '@store';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

export interface UseAuthLifecycleReturn {
  phase: AuthPhase;
  reLogin: () => void;
  error: Error | undefined;
}

function detectEmailUnverified(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('error') === AUTH_ERROR_TYPE.ACCESS_DENIED &&
    params.get('error_description') === AUTH_ERROR_MESSAGE.EMAIL_NOT_VERIFIED
  );
}

export function useAuthLifecycle(): UseAuthLifecycleReturn {
  const router = useRouter();
  const { isLoading, isAuthenticated, loginWithRedirect, logout, error } = useAuth0();
  const { verdict: probeVerdict } = useAuthSessionProbe();
  const { accountMe, getAccountMe } = useAccountsService();
  const { registerReferral } = useAffiliateService();
  const imprvApi = useImprvApi();

  const { setAuthPhase, setImprvApi } = useStore(
    (state) => ({
      setAuthPhase: state.setAuthPhase,
      setImprvApi: state.setImprvApi
    }),
    shallow
  );

  const [loggingOut, setLoggingOut] = useState(false);
  const [sessionInvalid, setSessionInvalid] = useState(false);
  // Read once at mount — safe ONLY because the verify-email route is a PUBLIC_ROUTE
  // in pages/_app.jsx, so this hook unmounts before the flag could get stuck.
  const [emailUnverified] = useState<boolean>(detectEmailUnverified);

  const phase = deriveAuthPhase({
    isLoading,
    isAuthenticated,
    hasAccountMe: !!accountMe,
    probeVerdict,
    loggingOut,
    sessionInvalid,
    emailUnverified
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setImprvApi(imprvApi);
  }, []);

  const lastPhaseRef = useRef<AuthPhase | null>(null);
  useEffect(() => {
    if (lastPhaseRef.current === phase) return;
    lastPhaseRef.current = phase;
    setAuthPhase(phase);
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'auth phase changed',
      data: { phase },
      level: 'info'
    });
  }, [phase, setAuthPhase]);

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    return eventEmitter.on(EventType.LOGOUT_REQUESTED, () => {
      setLoggingOut(true);
      Promise.resolve(logoutRef.current()).catch(() => setLoggingOut(false));
    });
  }, []);

  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  const loggingOutRef = useRef(loggingOut);
  loggingOutRef.current = loggingOut;
  useEffect(() => {
    return eventEmitter.on(EventType.AUTH_SESSION_INVALID, () => {
      if (isAuthenticatedRef.current && !loggingOutRef.current) setSessionInvalid(true);
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isLoading && !accountMe) getAccountMe();
  }, [isAuthenticated, isLoading, accountMe, getAccountMe]);

  // Once authenticated, claim any affiliator lead captured pre-login (first-touch wins,
  // so a repeat is a harmless no-op). Clear the code once the backend has decided.
  const referralClaimedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || isLoading || !accountMe || referralClaimedRef.current) return;
    const code = getStoredReferralCode();
    if (!code) return;
    referralClaimedRef.current = true;
    registerReferral(code)
      .then(() => clearReferralCode())
      .catch(() => {
        // network/other error — allow a retry on the next mount
        referralClaimedRef.current = false;
      });
  }, [isAuthenticated, isLoading, accountMe, registerReferral]);

  const redirectedRef = useRef(false);
  const reLogin = useCallback(() => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    loginWithRedirect({ appState: { returnTo: buildReturnTo(window.location) } }).catch(() => {
      redirectedRef.current = false;
    });
  }, [loginWithRedirect]);

  useEffect(() => {
    if (phase === AuthPhase.UNAUTHENTICATED) reLogin();
  }, [phase, reLogin]);

  useEffect(() => {
    if (phase === AuthPhase.EMAIL_UNVERIFIED)
      router.replace({ pathname: ROUTER_NAME.VERIFY_EMAIL });
  }, [phase, router]);

  return { phase, reLogin, error };
}
